class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.openTimer = null;
    this.closeTimer = null;

    this.addEventListener('keydown', this.onKeyDown.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this));

    // Hover-intent via cursor-position tracking. Element-based mouseenter/
    // leave fails here because the spatial gap between summary and the
    // absolutely-positioned content panel (the .header padding-bottom)
    // belongs to no element of the menu's DOM subtree — slow movement
    // through that gap exceeds any reasonable closeTimer.
    //
    // Strategy: open on summary hover, then track document.mousemove
    // while open. Cancel closeTimer when cursor is in summary, content,
    // or the bridge rect spanning the gap. Start closeTimer when outside
    // all three.
    this.summary.addEventListener('mouseenter', this.onSummaryEnter.bind(this));
    this.summary.addEventListener('mouseleave', this.onSummaryLeave.bind(this));
    document.addEventListener('mousemove', this.onCursorMove.bind(this));
  }

  supportsHoverIntent() {
    return window.matchMedia('(hover: hover)').matches &&
           window.matchMedia('(min-width: 990px)').matches;
  }

  onSummaryEnter() {
    if (!this.supportsHoverIntent()) return;
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    if (this.mainDetailsToggle.open) return;
    this.openTimer = setTimeout(() => {
      this.mainDetailsToggle.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
    }, 100);
  }

  onSummaryLeave() {
    // Cancel pending open if cursor leaves summary before openTimer fires.
    if (!this.mainDetailsToggle.open) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  onCursorMove(event) {
    if (!this.supportsHoverIntent()) return;
    if (!this.mainDetailsToggle.open) return;

    if (this.isInHoverZone(event.clientX, event.clientY)) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    } else if (!this.closeTimer) {
      this.closeTimer = setTimeout(() => this.close(), 200);
    }
  }

  isInHoverZone(x, y) {
    if (!this.content) return false;
    const s = this.summary.getBoundingClientRect();
    const c = this.content.getBoundingClientRect();
    const inSummary = y >= s.top && y <= s.bottom && x >= s.left && x <= s.right;
    const inContent = y >= c.top && y <= c.bottom && x >= c.left && x <= c.right;
    // Bridge: spans summary's horizontal range from summary-bottom to content-top.
    // This is the spatial gap that no element claims; we manually treat it
    // as part of the hover zone so slow-mover cursors don't trigger close.
    const inBridge = y >= s.bottom && y <= c.top && x >= s.left && x <= s.right;
    return inSummary || inContent || inBridge;
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.mainDetailsToggle.open) {
      this.close();
      this.summary.focus();
    }
  }

  onDocumentClick(event) {
    if (!this.mainDetailsToggle.open) return;
    if (this.contains(event.target)) return;
    this.close();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
